import { useState } from "react";
import { X, Camera, Maximize2, VideoOff, Grid2x2, LayoutGrid } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import CameraView from "@/components/CameraView";

const camName = (c) => c.name || c.number || (c.id || "").slice(0, 8);

export default function CameraWall({ items, onClose }) {
  const { t } = useI18n();
  const [big, setBig] = useState(null);
  const [mode, setMode] = useState("grid"); // grid | quad
  const cams = (items || []).filter((it) => (it.camera_url || "").trim() || (it.rtsp_url || "").trim());
  const [slots, setSlots] = useState(() => [0, 1, 2, 3].map((i) => cams[i]?.id || ""));

  const setSlot = (i, id) => setSlots((s) => s.map((v, k) => (k === i ? id : v)));
  const camById = (id) => cams.find((c) => c.id === id);

  return (
    <div className="fixed inset-0 z-[2000] bg-slate-950/95 flex flex-col" data-testid="camera-wall">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 shrink-0">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
          <Camera size={18} className="text-cyan-400" /> {t("Ściana kamer — Szafy / ODF")}
          <span className="text-xs text-slate-500 bg-slate-800 rounded px-1.5 py-0.5 font-mono">{cams.length}</span>
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button onClick={() => setMode("grid")} data-testid="camera-mode-grid"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${mode === "grid" ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              <LayoutGrid size={14} /> {t("Miniatury")}
            </button>
            <button onClick={() => setMode("quad")} data-testid="camera-mode-quad"
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${mode === "quad" ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              <Grid2x2 size={14} /> {t("Podgląd 2×2 (dyżur)")}
            </button>
          </div>
          <button onClick={onClose} data-testid="camera-wall-close" className="text-slate-400 hover:text-white"><X size={22} /></button>
        </div>
      </div>

      {cams.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-2" data-testid="camera-wall-empty">
          <VideoOff size={40} />
          <p className="text-sm">{t("Żadna szafa nie ma ustawionego strumienia kamery (pole „Strumień kamery IP” lub „RTSP”).")}</p>
        </div>
      ) : mode === "quad" ? (
        <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-2 p-2 min-h-0" data-testid="camera-quad">
          {[0, 1, 2, 3].map((i) => {
            const c = camById(slots[i]);
            return (
              <div key={i} className="relative bg-black rounded-lg border border-slate-800 overflow-hidden min-h-0" data-testid={`camera-quad-slot-${i}`}>
                {c ? <CameraView item={c} className="w-full h-full object-contain" style={{ position: "absolute", inset: 0 }} testid={`camera-quad-view-${i}`} />
                   : <div className="w-full h-full flex items-center justify-center text-slate-600 text-sm">{t("Pusty ekran")}</div>}
                <select value={slots[i]} onChange={(e) => setSlot(i, e.target.value)} data-testid={`camera-quad-select-${i}`}
                  className="absolute top-2 left-2 bg-slate-900/90 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 max-w-[70%]">
                  <option value="">— {t("Wybierz szafę")} —</option>
                  {cams.map((cc) => <option key={cc.id} value={cc.id}>{camName(cc)}</option>)}
                </select>
                {c && <span className="absolute bottom-2 left-2 bg-slate-900/80 rounded px-2 py-0.5 text-xs">{camName(c)}</span>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-4 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {cams.map((c) => (
            <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden group" data-testid={`camera-tile-${c.id}`}>
              <div className="relative bg-black" style={{ aspectRatio: "16/9" }}>
                <CameraView item={c} className="w-full h-full object-contain" style={{ position: "absolute", inset: 0 }} />
                <button onClick={() => setBig(c)} data-testid={`camera-enlarge-${c.id}`} title={t("Powiększ")}
                  className="absolute top-2 right-2 bg-slate-900/80 hover:bg-cyan-600 rounded p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 size={15} />
                </button>
              </div>
              <div className="px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{camName(c)}</span>
                {c.miejscowosc && <span className="text-[11px] text-slate-500 truncate">{c.miejscowosc}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {big && (
        <div className="fixed inset-0 z-[2100] bg-black/90 flex flex-col p-4" data-testid="camera-single" onClick={() => setBig(null)}>
          <div className="flex items-center justify-between mb-2 shrink-0">
            <span className="font-heading font-bold text-lg flex items-center gap-2">
              <Camera size={18} className="text-cyan-400" /> {camName(big)}
            </span>
            <button onClick={() => setBig(null)} data-testid="camera-single-close" className="text-slate-300 hover:text-white"><X size={24} /></button>
          </div>
          <div className="flex-1 flex items-center justify-center min-h-0" onClick={(e) => e.stopPropagation()}>
            <CameraView item={big} className="max-w-full max-h-full rounded border border-slate-700 bg-black" />
          </div>
        </div>
      )}
    </div>
  );
}
